/* eslint-disable @typescript-eslint/no-explicit-any */
import { Grid, IconButton, InputAdornment, Stack } from '@mui/material';
import CustomInputLabel from 'components/cmn/CustomInputLabel';
import CustomOutlinedInput from 'components/cmn/CustomOutlinedInput';
import DialogBody from 'components/dialog/DialogBody';
import DialogContent from 'components/dialog/DialogContent';
import DialogTitle from 'components/dialog/DialogTitle';
import { handlePasswordValidate, passwordDialog } from 'libs/mindUtils';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { memo, useState, useMemo } from 'react';
import { useForm, Controller, FieldErrors, UseFormReset, useWatch } from 'react-hook-form';
import { AccountValues } from 'pages/admin/account';
import LoginButton from 'components/login/LoginButton';
import { useAppDispatch } from 'store';
import { closeDialog } from 'store/dialog/dialogSlice';
import { showMessage } from 'store/message/messageSlice';
import { postLoginCheck } from 'store/login/loginSlice';

interface IPasswordValues {
  originPassword: string;
  password: string;
  confirmPassword: string;
}
interface IPasswordDialogProps {
  values: {
    name?: string | undefined;
    email?: string | undefined;
    mobile?: string | undefined;
    password?: string | undefined;
    compName?: string | undefined;
    compRole?: string | undefined;
  };
  reset: UseFormReset<AccountValues>;
}
function PasswordDialog({ values, reset }: IPasswordDialogProps): JSX.Element {
  const dispatch = useAppDispatch();
  const [showOriginPassword, setShowOriginPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validateOrigin, setValidateOrigin] = useState(false);

  const {
    handleSubmit,
    formState: { errors },
    control,
    getValues,
  } = useForm<IPasswordValues>({
    defaultValues: {
      originPassword: '',
      password: '',
      confirmPassword: '',
    },
    mode: 'onChange',
  });
  const passwordInfos = useWatch({ control });

  const handleClickShowPassword = (id: string) => {
    if (id === 'originPassword') {
      setShowOriginPassword(!showOriginPassword);
    } else if (id === 'password') {
      setShowPassword(!showPassword);
    } else if (id === 'confirmPassword') {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };
  const handleTextType = (fieldId: string, fieldType: string): string => {
    if (fieldId === 'originPassword') {
      return showOriginPassword ? 'text' : 'password';
    }
    if (fieldId === 'password') {
      return showPassword ? 'text' : 'password';
    }
    if (fieldId === 'confirmPassword') {
      return showConfirmPassword ? 'text' : 'password';
    }
    return fieldType;
  };
  const handleConfirmPassword = (_value: string) => {
    return _value === getValues('password') || '비밀번호가 일치하지 않습니다.';
  };
  const handleOriginPasswordValidate = async (_value: string) => {
    const tmpEmail = values.email || '';
    const tmpResult = await dispatch(
      postLoginCheck({
        email: tmpEmail,
        password: _value,
      }),
    ).unwrap();
    setValidateOrigin(tmpResult.status === 200);
    return tmpResult.status === 200 || '비밀번호가 일치하지 않습니다.';
  };
  const handleValidate = (
    fieldId: string,
    _value: string,
    fieldValidate?: ((value: string) => boolean | string) | undefined,
  ): any => {
    if (fieldId === 'originPassword') {
      return handleOriginPasswordValidate(_value);
    }
    if (fieldId === 'confirmPassword') {
      return handleConfirmPassword(_value);
    }
    if (fieldId === 'password') {
      return _value === values.password
        ? '이전 비밀번호와 다른 비밀번호를 입력해주세요.'
        : handlePasswordValidate(_value);
    }
    return fieldValidate;
  };
  const handleEndAdornment = (fieldId: string) => {
    if (['originPassword', 'password', 'confirmPassword'].includes(fieldId)) {
      return (
        <InputAdornment position="end">
          <IconButton
            aria-label="toggle password visibility"
            onClick={() => handleClickShowPassword(fieldId)}
            edge="end"
            size="small"
          >
            {(fieldId === 'originPassword' && showOriginPassword) ||
            (fieldId === 'password' && showPassword) ||
            (fieldId === 'confirmPassword' && showConfirmPassword) ? (
              <Visibility />
            ) : (
              <VisibilityOff />
            )}
          </IconButton>
        </InputAdornment>
      );
    }
    return null;
  };
  const handleDisabledCheck = (_id: string) => {
    if (_id === 'originPassword') return false;
    if (['password', 'confirmPassword'].includes(_id) && validateOrigin) {
      return false;
    }
    return true;
  };
  const onSubmit = (_data: any) => {
    if (validateOrigin) {
      reset({
        ...values,
        password: _data.password,
        originPassword: _data.originPassword,
      });
      dispatch(closeDialog());
      dispatch(
        showMessage({
          message: '변경된 비밀번호를 적용하시려면 회원 정보 변경 버튼을 눌러주세요.',
          variant: 'success',
        }),
      );
    }
  };
  const isDisabled = useMemo(
    () =>
      Object.keys(passwordInfos).some(field => {
        const value = passwordInfos[field as keyof IPasswordValues];
        return value !== undefined && value.length === 0;
      }),
    [passwordInfos],
  );
  return (
    <DialogContent width={{ desktop: 500 }} height={{ desktop: 'fit-content', mobile: 'fit-content' }}>
      <DialogTitle hideCloseButton>비밀번호 변경</DialogTitle>
      <DialogBody style={{ overflowX: 'hidden', overflowY: 'hidden !important' }}>
        <Stack direction="row" spacing={3.75}>
          <Grid container>
            <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
              {passwordDialog.map(field => (
                <Grid item xs={12} mb={2.5} key={field.id}>
                  <Grid item xs={12} key={field.id}>
                    <Stack spacing={1.25}>
                      <Controller
                        name={field.id as keyof IPasswordValues}
                        control={control}
                        rules={{
                          required: field.required,
                          validate: (value: string) => handleValidate(field.id, value, field.validate),
                        }}
                        render={({ field: { onChange, value } }) => (
                          <>
                            <CustomInputLabel htmlFor={field.id} label={field.label} />
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                              <CustomOutlinedInput
                                id={field.id}
                                value={value}
                                onChange={e => {
                                  let tmpValue = e.target.value;
                                  if (field.id === 'mobile') {
                                    tmpValue = tmpValue.replace(/[^0-9]/g, '');
                                    tmpValue = tmpValue.replace(/-/g, '');
                                    tmpValue = tmpValue.substring(0, 11);
                                  }
                                  onChange(tmpValue);
                                }}
                                placeholder={field.placeholder}
                                fullWidth
                                error={!!(errors as FieldErrors<IPasswordValues>)[field.id as keyof IPasswordValues]}
                                message={
                                  (errors as FieldErrors<IPasswordValues>)[field.id as keyof IPasswordValues]
                                    ?.message || ''
                                }
                                type={handleTextType(field.id, field.type)}
                                autoComplete="off"
                                endAdornment={handleEndAdornment(field.id)}
                                disabled={handleDisabledCheck(field.id)}
                              />
                            </Stack>
                          </>
                        )}
                      />
                    </Stack>
                  </Grid>
                </Grid>
              ))}
              <LoginButton disabled={isDisabled || Object.keys(errors).length > 0} sx={{ height: 40 }}>
                비밀번호 변경
              </LoginButton>
            </form>
          </Grid>
        </Stack>
      </DialogBody>
    </DialogContent>
  );
}

export default memo(PasswordDialog);
